import { TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { DotLegalButton, DotLegalOverflowMenu, DotLegalTooltip, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import DotLegalPaper from "../../../common/components/dotLegalPaper/DotLegalPaper.tsx";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import { UserAccessTableViewModel } from "./SystemAccessTab.types.ts";
import { useSystemAccessTab } from "./SystemAccessTab.hooks.ts";
import { Box } from "@mui/system";
import RemoveFromExcludedListDialog from "./removeFromExcludedListDialog/RemoveFromExcludedListDialog.tsx";
import ExcludeAccessDialog from "./excludeAccessDialog/ExcludeAccessDialog.tsx";

export interface SystemAccessTabProps {
    systemId: string;
}

function SystemAccessTab(props: SystemAccessTabProps) {
    const urlProvider = useUrlProvider();

    const { permissions } = useUserContext();
    const { translateString } = useTranslation();
    const {
        isLoading,
        rows,
        accessStepOrder,
        canNavigateToAccessStep,
        canManageExcludedUsers,
        getMenuItems,
        showGrantAccessDialog,
        showExcludeAccessDialog,
        onAccessExcluded,
        onRemoveFromExcludedList,
        onCloseDialog,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useSystemAccessTab(props);

    function getTableHeader() {
        const headers = Array<ITableHeader<UserAccessTableViewModel>>();
        headers.push({ property: "fullName", text: translateString("name"), align: "left", showOnMobile: true });
        headers.push({ property: "email", text: translateString("email"), align: "left", showOnMobile: true });
        headers.push({ property: "accessOrigins", text: translateString("accessGrantedFrom"), align: "left", showOnMobile: true });
        headers.push({ property: "hasAccess", text: translateString("access"), align: "left", showOnMobile: true });

        return headers;
    }

    return (
        <React.Fragment>
            <DotLegalPaper>
                <DotLegalTableWithFilterBar
                    filterBar={{
                        isLoading: isLoading,
                        filterData: getFilterOptions(),
                        fetchFilterData: true,
                        onMenuClose: onFilterChange,
                        onRemove: onFilterChange,
                        pinnedFilterIds: pinnedFilterIds,
                        onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                    }}
                    data={rows}
                    rowsPerPage={30}
                    headers={getTableHeader()}
                    defaultOrderBy={"fullName"}
                    defaultOrder={"asc"}
                    isLoading={isLoading}
                    clickableRows={false}
                    hasOverflowMenu
                    emptyText={hasActiveFilters ? translateString("noDataMatchingCriteria") : undefined}
                    renderRow={(row) => (
                        <TableRow key={row.id} inactive={!row.hasAccess} inactiveTooltip={translateString("userExcludedFromSystem")}>
                            <TableCell>
                                {row.fullName}
                                {row.isActive ? "" : ` (${translateString("deactivated")})`}
                            </TableCell>
                            <TableCell>{row.email}</TableCell>
                            <TableCell>{row.accessOriginsString}</TableCell>
                            <TableCell>{row.hasAccessString}</TableCell>
                            <TableCell align={"right"}>
                                {permissions.systemPermissions.edit && canManageExcludedUsers && <DotLegalOverflowMenu items={getMenuItems(row)} />}
                            </TableCell>
                        </TableRow>
                    )}
                />
                <Box sx={{ display: "flex", justifyContent: "end", mt: 2 }}>
                    <DotLegalButton
                        disabled={isLoading || !canNavigateToAccessStep}
                        buttonType={"primary"}
                        href={canNavigateToAccessStep ? urlProvider.getSystemEditUrl(props.systemId, accessStepOrder) : undefined}
                        toolTip={!canNavigateToAccessStep ? translateString("roleDoesNotHaveAccessToFieldsOnStep") : undefined}
                    >
                        {translateString("manageUserAccess")}
                    </DotLegalButton>
                </Box>
            </DotLegalPaper>

            {showExcludeAccessDialog && (
                <ExcludeAccessDialog
                    systemId={props.systemId}
                    userId={showExcludeAccessDialog.id}
                    userFullName={showExcludeAccessDialog.fullName}
                    onCloseDialog={onCloseDialog}
                    onAccessExcluded={onAccessExcluded}
                />
            )}

            {showGrantAccessDialog && (
                <RemoveFromExcludedListDialog
                    systemId={props.systemId}
                    userId={showGrantAccessDialog.id}
                    userFullName={showGrantAccessDialog.fullName}
                    onCloseDialog={onCloseDialog}
                    onRemoveFromExcludedList={onRemoveFromExcludedList}
                />
            )}
        </React.Fragment>
    );
}

export default SystemAccessTab;
